/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable import/order */
import React, { useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import Lottie from 'lottie-react';
import { isEmpty } from 'ramda';

import { GET_SURVEY_BY_PROGRAM, GET_INDIVIDUAL_INFO } from 'apollo/queries';
import { ONBOARDING_STATUS, KEYS } from 'components/evieOnboarding/utils/constants';
import { useLocalStorage } from 'utils/hooks/useLocalStorage';
import { usePremiumEntitlement } from 'utils/hooks/usePremiumEntitlement';
import { useGetParams } from 'components/evieOnboarding/utils/useGetParams';
import { ROUTES } from 'utils/constants';
import { useTabSettings } from 'utils/hooks/useTabSettings';
import { useFcPixel } from 'utils/hooks/useFcPixel';
import { useIndividualSignup } from 'components/evieOnboarding/utils/useIndividualSignup';
import { useToggle } from 'utils/hooks/useToggle';
import { handleTrackEvent } from 'components/evieOnboarding/utils/handleTrackEvent';
import { setAnalyticsUser } from 'utils/analytics';
import heartLoader from 'assets/heart-loader.json';

import Layout from 'components/evieOnboarding/Layout';
import Welcome from 'components/evieOnboarding/Welcome';
import Survey from 'components/evieOnboarding/Survey';
import TestResultsScreen from 'components/evieOnboarding/TestResultsScreen';
import SignupScreen from 'components/evieOnboarding/SignupScreen';
import Plans from 'components/evieOnboarding/Plans'; // Perks, Trial or Offerings screen, depending on the user's path
import LoadingScreen from 'components/evieOnboarding/LoadingScreen';
// import FollowupQuestion from 'components/evieOnboarding/FollowupQuestion';
import OfferingIndex from 'components/evieOnboarding/OfferingIndex';
import { EvieOnboardingProvider, useEvieOnboardingContext } from 'components/evieOnboarding/EvieOnboardingProvider';

import './evie-onboarding.scss';

const EvieProgramWrapper = () => (
  <EvieOnboardingProvider>
    <EvieOnboarding />
  </EvieOnboardingProvider>
);

const EvieOnboarding = () => {
  useTabSettings('Evie', '/evie-favicon.ico');
  const { params, setParams } = useEvieOnboardingContext();
  const formatedParams = params ? `${params}&` : '';
  const { urlParams, source, step, group, question, urlToken, partner, push } = useGetParams();

  const [status, setStatus] = useLocalStorage(KEYS.ONBOARDING_STATUS, ONBOARDING_STATUS.WELCOME);
  const [, setContent] = useLocalStorage(KEYS.CONTENT);
  const [userToken, setUserToken] = useLocalStorage(KEYS.USER_TOKEN);
  const [, setSource] = useLocalStorage(KEYS.SOURCE);
  const [, setUserId] = useLocalStorage(KEYS.USER_ID);
  const [path, setPath] = useLocalStorage(KEYS.PATH);
  const [, setEmail] = useLocalStorage(KEYS.USER_EMAIL);
  const [, setPartner] = useLocalStorage(KEYS.PARTNER);

  // In-app entries carry no URL token, so fall back to the stored session.
  const token = urlToken || userToken;
  const { loading: individualLoading, data: individualData = {} } = useQuery(GET_INDIVIDUAL_INFO, {
    skip: !token,
    context: {
      headers: {
        authorization: token ? `Bearer ${token}` : '',
        role: 'individual',
      },
    },
  });
  const { getIndividualInfo = {} } = individualData;
  const {
    id: individualId = null,
    email = '',
    oldProgramPayment = false,
    currentProgram: { planKey = null } = {},
  } = getIndividualInfo;
  const { isPremium, isPending } = usePremiumEntitlement({ appUserId: individualId, oldProgramPayment });
  const [getSurvey, { loading, data = {} }] = useLazyQuery(GET_SURVEY_BY_PROGRAM);
  const { getSurveyByProgram = {} } = data;
  const { individualSignup, spinner, errors } = useIndividualSignup(setStatus);

  const [isSendingSurvey, setIsSendingSurvey] = useToggle();
  const { view } = useGetParams();
  // const [displayFollowupQuestion, setDisplayFollowupQuestion] = useToggle();
  // const promiseResolver = useRef(null);
  // console.log('PROMiSE: ', promiseResolver);

  useFcPixel();

  useEffect(() => {
    if ((userToken || urlToken) && !urlParams.includes('plan_step')) {
      setParams(urlParams);
    }
  }, []);

  useEffect(() => {
    if (individualId) {
      setUserId(individualId);
      setAnalyticsUser(individualId);
    }
    if (planKey) {
      setPath(planKey);
    }
    if (email) {
      setEmail(email);
    }
  }, [individualId, planKey, email]);

  useEffect(() => {
    if (!path) {
      handleTrackEvent('Initial visit');
      setSource(source);
    }
  }, [path]);

  useEffect(() => {
    // urlParams is not a dep: it changes every survey step and would trap a logged-out user on Welcome.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (!userToken && !urlParams.includes('plan_step')) {
      setStatus(ONBOARDING_STATUS.WELCOME);
      push(`${ROUTES.EVIE_ONBOARDING}?${params}`);
    }
  }, [source, userToken, params]);

  useEffect(() => {
    if (userToken || urlToken) {
      if (urlToken) {
        setUserToken(urlToken);
      }
      setStatus(ONBOARDING_STATUS.PLANS);
      // plan_step in the URL is an explicit destination (Arm A's post-signup landing), so leave it.
      if (!urlParams.includes('plan_step')) {
        // `params` still holds the previous visit here, so the live URL wins to keep `entry`/`view`.
        const nextParams = urlParams || params;
        push(`${ROUTES.EVIE_ONBOARDING}?${nextParams ? `${nextParams}&` : ''}plan_step=1`);
      }
    }
  }, [urlToken, userToken, params, urlParams]);

  useEffect(() => {
    if (!partner) return;
    (async () => {
      // await flushes the localStorage write before navigation unmounts us (see useLocalStorage.js).
      await setPartner(partner);
      push(`${ROUTES.EVIE_SET_PHONE}?partner=${partner}`);
    })();
  }, [partner]);

  useEffect(() => {
    if (!individualId || isPending || !isPremium) return;
    (async () => {
      await setStatus(ONBOARDING_STATUS.PLANS);
      push(ROUTES.EVIE_DOWNLOAD);
    })();
  }, [individualId, isPending, isPremium]);

  /* const handleFollowupQuestion = () => {
    setDisplayFollowupQuestion();
    promiseResolver.current();
    promiseResolver.current = null;
  }; */

  const handleStartSurvey = (selectedPath) => {
    getSurvey({ variables: { planKey: selectedPath } });
    setStatus(ONBOARDING_STATUS.SURVEY);
    handleTrackEvent('Step clicked', { number: step + 1 });
    handleTrackEvent('Survey started');
    push(`${ROUTES.EVIE_ONBOARDING}?${formatedParams}step=${step + 1}&group=${group}&question=${question}`);
  };

  const handleCompleteSurvey = (surveyData) => {
    setStatus(ONBOARDING_STATUS.TEST_RESULTS);
    setContent(surveyData);
    handleTrackEvent('Step clicked', { number: step + 1 });
    handleTrackEvent('Survey finished');
    push(`${ROUTES.EVIE_ONBOARDING}?${formatedParams}step=${step + 1}`);
  };

  const handleGoToSignUp = async () => {
    setIsSendingSurvey(true);
    /* await new Promise((resolve) => {
      setTimeout(() => {
        promiseResolver.current = resolve;
        setDisplayFollowupQuestion();
      }, 3000);
    }); */
    await new Promise((resolve) => {
      setTimeout(() => {
        setIsSendingSurvey(false);
        resolve();
      }, 9000);
    });
    setStatus(ONBOARDING_STATUS.SIGN_UP);
    handleTrackEvent('Step clicked', { number: step + 1 });
    handleTrackEvent('Test results screen');
    push(`${ROUTES.EVIE_ONBOARDING}?${formatedParams}step=${step + 1}`);
  };

  return (
    <>
      <Layout>
        {status === ONBOARDING_STATUS.WELCOME && <Welcome startSurvey={handleStartSurvey} />}
        {status === ONBOARDING_STATUS.SURVEY && !isEmpty(data) && (
          <Survey survey={getSurveyByProgram} completeSurvey={handleCompleteSurvey} />
        )}
        {status === ONBOARDING_STATUS.TEST_RESULTS && !isSendingSurvey && (
          <TestResultsScreen goToSignUp={handleGoToSignUp} />
        )}
        {status === ONBOARDING_STATUS.SIGN_UP && <SignupScreen signup={individualSignup} errors={errors} />}
        {status === ONBOARDING_STATUS.PLANS && !individualLoading && !isPending && !isPremium && (
          <>
            {view === 'plans' ? (
              <Plans userId={individualId} path={planKey} queryEmail={email} />
            ) : (
              <OfferingIndex userId={individualId} path={planKey} queryEmail={email} />
            )}
          </>
        )}
        {isSendingSurvey && <LoadingScreen />}
      </Layout>
      {(loading || spinner || individualLoading || isPending) && (
        <div className="onboarding-layout__lottie">
          <Lottie animationData={heartLoader} loop style={{ width: 140, height: 110 }} />
        </div>
      )}
      {/* {displayFollowupQuestion && (
        <FollowupQuestion
          modalIsOpen={displayFollowupQuestion}
          toggleModal={setDisplayFollowupQuestion}
          handleFollowupQuestion={handleFollowupQuestion}
        />
      )} */}
    </>
  );
};

export default EvieProgramWrapper;
